import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandMutation, useBandQuery } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

export type ImportSource = components["schemas"]["ImportSourceView"];
export type ReleaseImportsView = components["schemas"]["ReleaseImportsView"];
export type ReleaseImportView = components["schemas"]["ReleaseImportView"];
export type ReleaseImportItemView = components["schemas"]["ReleaseImportItemView"];
export type ReleaseImportItemInput = components["schemas"]["ReleaseImportItemInput"];

const importsKey = (releaseId: string) => ["release", releaseId, "imports"] as const;

/** Still moving: the worker has not finished with it. */
export function isImportInFlight(item: ReleaseImportItemView): boolean {
  return item.status === "queued" || item.status === "copying";
}

/**
 * `GET /bands/{bandId}/releases/{releaseId}/imports` (ART-369) — the sources (and each picker's key), the limits a
 * row is checked against, and the release's recent imports, newest first. Polls every two seconds while any file is
 * still copying, and stops by itself when they are all placed or failed.
 */
export function useReleaseImports(releaseId: string, enabled = true) {
  const { api } = useApp();
  return useBandQuery<ReleaseImportsView>(
    importsKey(releaseId),
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/releases/{releaseId}/imports", {
        params: { path: { bandId, releaseId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      enabled,
      refetchInterval: (q) =>
        q.state.data?.imports?.some((i) => i.items?.some(isImportInFlight)) ? 2_000 : false,
    },
  );
}

/** Queue the picked files. A 422 carries a reason per row (`items[n]`), surfaced by the caller beside that row. */
export function useCreateReleaseImport(releaseId: string) {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<ReleaseImportView, { provider: string; items: ReleaseImportItemInput[] }>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/releases/{releaseId}/imports", {
        params: { path: { bandId: band, releaseId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, ...importsKey(releaseId)] }) },
  );
}

/** Retry one failed file (or one stuck copying). Only while its picker link is still alive; the server says which. */
export function useRetryReleaseImportItem(releaseId: string) {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<ReleaseImportView, { importId: string; itemId: string }>(
    async (band, { importId, itemId }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/releases/{releaseId}/imports/{importId}/items/{itemId}/retry",
        { params: { path: { bandId: band, releaseId, importId, itemId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, ...importsKey(releaseId)] }) },
  );
}
